/**
 * PF Variant Picker — API-driven custom element.
 *
 * Fetches the canonical ProductSchema JSON from the Polyform backend (keyed by
 * SKU), renders the option fieldsets client-side, and drives variant selection
 * and cross-product navigation from the unified options[] model.
 * Contract: Polyform API ProductSchema.
 *
 * Attributes (set by pf-variant-picker.liquid):
 *   data-sku               Anchor SKU used to fetch the schema.
 *   data-schema-endpoint   URL template containing a "{sku}" placeholder.
 *   data-url               Product URL (e.g. /products/my-product).
 *   data-section-id        Shopify section ID for XHR section refresh.
 *   data-update-url        "true"/"false" — whether to push ?variant= to history.
 *   data-select-first-variant  Passed through from the section setting.
 *
 * Public methods:
 *   select(optionName, value)  Programmatically select an option value.
 *   getSelection()             Returns current { variant, selection } state.
 *
 * Events emitted (all bubble):
 *   on:variant:change   detail: { variant, selection, fallbackFrom?, form }
 *   on:variant:redirect detail: { url, handle, selection }
 *   on:picker:ready     detail: { variant, selection }
 */

import './pf-availability.js';
import './pf-cart-availability.js';
import { localizeEndpoint } from './pf-variant-picker/engine/api-base.js';
import {
  availabilityUrlFor,
  fetchAvailability,
  mergeAvailability,
} from './pf-variant-picker/engine/availability-fetch.js';
import {
  updateShipTime,
  updateAvailabilityLabel,
  updateAddToCartButton,
} from './pf-variant-picker/engine/availability-view.js';
import {
  dispatchVariantChange,
  dispatchVariantRedirect,
  dispatchPickerReady,
} from './pf-variant-picker/engine/events.js';
import {
  parseVariantMedia,
  syncGalleryToVariant,
} from './pf-variant-picker/engine/gallery-sync.js';
import { deriveLayout } from './pf-variant-picker/engine/layout.js';
import {
  renderPicker,
  isValueUnavailable,
  SWATCH_PLACEHOLDER_SVG,
} from './pf-variant-picker/engine/render.js';
import {
  buildSchemaUrl,
  fetchSchema,
} from './pf-variant-picker/engine/schema-fetch.js';
import { makeAccessor } from './pf-variant-picker/engine/schema-reader.js';
import {
  resolveSelection,
  hydrateFromUrl,
  initialSelection,
  findVariant,
} from './pf-variant-picker/engine/selection-resolver.js';
import {
  buildRedirectUrl,
  pushVariantUrl,
} from './pf-variant-picker/engine/url-sync.js';

let pickerUid = 0;
const CROSS_PRODUCT_LAYOUTS = new Set(['x-split', 'image-meta']);

function isCrossProductOption(opt) {
  if (opt?.scope === 'product') return true;
  if (opt?.scope === 'variant') return false;
  return CROSS_PRODUCT_LAYOUTS.has(deriveLayout(opt));
}

// Minimum time a loading spinner stays visible, so a fast/cached fetch still
// gives perceptible feedback instead of an imperceptible flash.
const MIN_SPINNER_MS = 400;

if (!customElements.get('pf-variant-picker')) {
  class VariantPicker extends HTMLElement {
    // ─── Lifecycle ────────────────────────────────────────────────────────

    connectedCallback() {
      this._uid = pickerUid += 1;
      this._section =
        this.closest('.js-quickbuy') ||
        this.closest('.cc-featured-product') ||
        this.closest('.shopify-section');
      this._variantMedia = parseVariantMedia(this.dataset.variantMedia);
      this._syncGallery(this.dataset.variantId);

      const sku = this.dataset.sku;
      const url = localizeEndpoint(
        buildSchemaUrl(this.dataset.schemaEndpoint, sku)
      );
      if (!url) {
        // No SKU / endpoint — leave the server-rendered price + ATC intact.
        this.setAttribute('loaded', '');
        return;
      }

      const controller = new AbortController();
      this._abort = controller;
      const mountTs = Date.now();

      // Render as soon as the schema arrives — availability (a separate, live,
      // uncacheable request) must NEVER block or delay the picker render.
      fetchSchema(url, controller.signal).then(
        (schema) => {
          // Keep the cold-load spinner visible for a perceptible minimum before
          // swapping in the picker (the schema may be served from cache).
          this._afterMinSpinner(mountTs, () => {
            // A slow fetch may resolve after the element was disconnected and
            // re-mounted; ignore it if a newer mount has superseded this one.
            if (this._abort !== controller || !this.isConnected) return;
            try {
              this._init(schema);
            } catch (err) {
              // A render/DOM error after a successful fetch — surface it clearly
              // rather than mislabelling it as a fetch failure.
              console.error('[pf-variant-picker] init error:', err);
              this.setAttribute('loaded', '');
            }
          });
        },
        (err) => {
          if (controller.signal.aborted || this._abort !== controller) return;
          // Graceful degradation: the base-theme SSR price/ATC for the anchor
          // variant remains functional even without the picker.
          console.warn(
            '[pf-variant-picker] schema fetch failed:',
            err?.message
          );
          this.setAttribute('loaded', '');
        }
      );

      // Availability streams in independently and enriches the already-rendered
      // picker (greying, ATC purchasability, ship time) once it lands. A failure
      // degrades gracefully — the picker is already interactive without it.
      const availUrl = availabilityUrlFor(
        this.dataset.availabilityEndpoint,
        sku
      );
      if (availUrl) {
        fetchAvailability(availUrl, controller.signal).then(
          (availability) => {
            if (this._abort !== controller || !this.isConnected) return;
            this._applyAvailability(availability);
          },
          (err) => {
            if (controller.signal.aborted || this._abort !== controller) return;
            console.warn(
              '[pf-variant-picker] availability fetch failed:',
              err?.message
            );
          }
        );
      }
    }

    _init(schema) {
      this._schema = schema;
      this._accessor = makeAccessor(schema);

      const { variant, selection } = hydrateFromUrl(
        schema,
        window.location.search
      );
      this._selection = selection;
      this._variant = variant;

      const markup = renderPicker(schema, selection, {
        uid: this._uid,
        currentUrl: this.dataset.url,
        sizeChart: this._sizeChartConfig(),
      });
      this.innerHTML = markup;

      // Nothing to choose (single variant, no cross-product axes).
      if (!markup) {
        dispatchPickerReady(this, { variant: this._variant, selection });
        this.setAttribute('loaded', '');
        // Availability may have raced ahead of the schema — apply it now.
        if (this._pendingAvailability) {
          this._applyAvailability(this._pendingAvailability);
          this._pendingAvailability = null;
        }
        return;
      }

      this._boundHandleChange = this._handleChange.bind(this);
      this._boundHandleLinkClick = this._handleLinkClick.bind(this);
      this.addEventListener('change', this._boundHandleChange);
      this.addEventListener('click', this._boundHandleLinkClick);
      // Swatch URLs that 404 collapse the swatch just like a missing URL —
      // swap the failed <img> for the generic placeholder. Image `error`
      // events don't bubble, so listen in the capture phase.
      this.addEventListener('error', this._handleSwatchImgError, true);

      this._applySelectionToDOM(selection);
      this.applyUnitPreferenceToSizePickerDimensions(this);
      this._syncUI();
      this._syncGallery();

      pushVariantUrl(
        this.dataset.url,
        this._variant?.id,
        selection,
        this.dataset.updateUrl !== 'false'
      );

      dispatchVariantChange(this, {
        variant: this._makeCompatVariant(),
        selection,
        form: this._section?.querySelector('.js-product-form') ?? null,
      });

      const anchor = initialSelection(schema);
      const selectionDiffersFromAnchor = Object.keys(selection).some(
        (k) => anchor[k] !== selection[k]
      );
      if (selectionDiffersFromAnchor) {
        this._sectionRender();
        this._refreshCrossAxes();
      }

      dispatchPickerReady(this, { variant: this._variant, selection });
      this.setAttribute('loaded', '');

      // Availability may have raced ahead of the schema — apply it now.
      if (this._pendingAvailability) {
        this._applyAvailability(this._pendingAvailability);
        this._pendingAvailability = null;
      }
    }

    disconnectedCallback() {
      this._abort?.abort();
      if (this._boundHandleChange) {
        this.removeEventListener('change', this._boundHandleChange);
      }
      if (this._boundHandleLinkClick) {
        this.removeEventListener('click', this._boundHandleLinkClick);
      }
    }

    // ─── Public API ───────────────────────────────────────────────────────

    select(optionName, value) {
      if (!this._schema) return;
      const result = resolveSelection(this._schema, this._selection, {
        optionName,
        newValue: value,
      });
      this._applyResult(result);
    }

    getSelection() {
      return { variant: this._variant, selection: { ...this._selection } };
    }

    // ─── Availability enrichment (post-render) ────────────────────────────

    // Merge the availability payload onto the (already-rendered) schema and
    // refresh the availability-dependent UI in place: greying, ATC state, ship
    // time. Never re-runs the initial render, so it can't delay first paint.
    // If it arrives before the schema, it's stashed and flushed from _init().
    _applyAvailability(availability) {
      if (!availability) return;
      if (!this._schema) {
        this._pendingAvailability = availability;
        return;
      }

      this._schema = mergeAvailability(this._schema, availability);
      this._accessor = makeAccessor(this._schema);
      // Re-point the current variant to its merged counterpart (now carrying
      // `available` + `estimatedShipTime`).
      if (this._selection) {
        this._variant =
          findVariant(this._schema.variants, this._selection) ?? this._variant;
      }

      this._applyGreying();
      this._syncUI();
    }

    // Resolve the "See size chart" link config for measurement-bearing options.
    // Returns null (so no link renders) unless the host page actually contains
    // the anchor target — the Polyform size-chart section emits <h2 id="size-chart">
    // only on products that have one. Anchor/label are overridable via
    // data-size-chart-anchor / data-size-chart-label on the shell.
    _sizeChartConfig() {
      const anchor = this.dataset.sizeChartAnchor || '#size-chart';
      const targetId = anchor.replace(/^#/, '');
      if (!targetId || !document.getElementById(targetId)) return null;
      return {
        anchor,
        label: this.dataset.sizeChartLabel || 'See size chart',
      };
    }

    // Toggle the greyed/unavailable classes on the rendered items to reflect
    // merged availability, without re-rendering (preserves focus/scroll).
    _applyGreying() {
      if (!this._schema) return;
      this.querySelectorAll('[data-pf-option-name][data-pf-raw-value]').forEach(
        (el) => {
          const optName = el.dataset.pfOptionName;
          const value = this._accessor?.value(optName, el.dataset.pfRawValue);
          if (!value) return;
          const unavailable = isValueUnavailable(
            this._schema,
            optName,
            value,
            this._selection
          );
          el.closest('li.pf-picker__item')?.classList.toggle(
            'pf-picker__item--unavailable',
            unavailable
          );
          if (el.tagName === 'INPUT') {
            el.classList.toggle('is-unavailable', unavailable);
          }
        }
      );
    }

    // ─── Event handling ───────────────────────────────────────────────────

    // A swatch <img> whose URL fails to load would collapse the swatch (and
    // show the browser's broken-image glyph); replace it in place with the
    // generic placeholder SVG so the cell keeps its shape.
    _handleSwatchImgError(evt) {
      const img = evt.target;
      if (!(img instanceof HTMLImageElement)) return;
      if (!img.matches('.pf-picker__label img')) return;
      if (img.dataset.pfPlaceholderApplied) return;
      img.dataset.pfPlaceholderApplied = '1';
      img.insertAdjacentHTML('afterend', SWATCH_PLACEHOLDER_SVG);
      img.remove();
    }

    _handleChange(evt) {
      if (!this._schema) return;
      const input = evt.target.closest(
        'input[type="radio"][data-pf-option-name][data-pf-raw-value]'
      );
      if (!input) return;

      const result = resolveSelection(this._schema, this._selection, {
        optionName: input.dataset.pfOptionName,
        newValue: input.dataset.pfRawValue,
      });
      this._applyResult(result);
    }

    _handleLinkClick(evt) {
      if (!this._schema) return;
      const blocked = evt.target.closest('.pf-picker__item--blocked');
      if (blocked && this.contains(blocked)) {
        evt.preventDefault();
        this._explainBlocked(blocked);
        return;
      }
      this._clearBlocked();
      const link = evt.target.closest('a.pf-picker__label');
      if (!link || !this.contains(link)) return;

      evt.preventDefault();
      if (link.classList.contains('is-active')) return;

      this._applyResult(
        resolveSelection(this._schema, this._selection, {
          optionName: link.dataset.pfOptionName,
          newValue: link.dataset.pfRawValue,
        })
      );
    }

    _explainBlocked(item) {
      this._clearBlocked();
      item.classList.add('pf-picker__item--told');
      const line = document.createElement('p');
      line.className = 'pf-picker__why-line';
      line.setAttribute('role', 'status');
      line.innerHTML = item.dataset.pfBlocked;
      item.closest('ul')?.after(line);
    }

    _clearBlocked() {
      this.querySelectorAll('.pf-picker__item--told').forEach((el) =>
        el.classList.remove('pf-picker__item--told')
      );
      this.querySelectorAll('.pf-picker__why-line').forEach((el) =>
        el.remove()
      );
    }

    // ─── Result application ───────────────────────────────────────────────

    _applyResult(result) {
      if (!result) return;
      this._clearBlocked();

      if (result.redirect) {
        const url = buildRedirectUrl(
          result.redirect.handle,
          result.redirect.selection
        );
        dispatchVariantRedirect(this, {
          url,
          handle: result.redirect.handle,
          selection: result.redirect.selection,
        });
        if (this.dataset.updateUrl !== 'false') window.location.assign(url);
        return;
      }

      this._selection = result.selection ?? this._selection;
      this._variant = result.variant ?? null;

      this._applySelectionToDOM(this._selection);
      this._syncUI();
      this._syncGallery();

      pushVariantUrl(
        this.dataset.url,
        this._variant?.id,
        this._selection,
        this.dataset.updateUrl !== 'false'
      );

      this._sectionRender(result.fallbackFrom);
      this._refreshCrossAxes();
    }

    // Run `fn` once a spinner started at `startTs` has been visible for at least
    // MIN_SPINNER_MS (so cached/fast fetches still show perceptible feedback).
    _afterMinSpinner(startTs, fn) {
      const wait = Math.max(0, MIN_SPINNER_MS - (Date.now() - startTs));
      if (wait) setTimeout(fn, wait);
      else fn();
    }

    _refreshCrossAxes() {
      const sku = this._variant?.sku;
      if (!sku || !this._schema) return;
      const url = localizeEndpoint(
        buildSchemaUrl(this.dataset.schemaEndpoint, sku)
      );
      if (!url) return;

      const token = (this._crossAxisToken = (this._crossAxisToken ?? 0) + 1);

      const loadingFieldsets = [
        ...this.querySelectorAll('fieldset[data-pf-option-name]'),
      ].filter((fs) => {
        const opt = this._schema.options.find(
          (o) => o.name === fs.dataset.pfOptionName
        );
        return opt && isCrossProductOption(opt);
      });
      loadingFieldsets.forEach((fs) => fs.setAttribute('data-pf-loading', ''));
      const start = Date.now();
      // Only the latest click owns the spinner — a stale response must not clear
      // a spinner that a newer click just set.
      const clearLoading = () => {
        if (token === this._crossAxisToken) {
          loadingFieldsets.forEach((fs) =>
            fs.removeAttribute('data-pf-loading')
          );
        }
      };

      fetchSchema(url, this._abort?.signal).then(
        (fresh) => {
          // Hold the spinner for the minimum, then swap in the fresh fieldsets
          // (the swap replaces the spinning fieldsets, so this also ends it).
          this._afterMinSpinner(start, () => {
            if (
              !this.isConnected ||
              token !== this._crossAxisToken ||
              !this._schema
            ) {
              clearLoading();
              return;
            }

            const linkAxes = this._schema.options.filter((o) =>
              isCrossProductOption(o)
            );
            let changed = false;
            for (const opt of linkAxes) {
              const freshOpt = fresh.options.find((o) => o.name === opt.name);
              if (freshOpt?.values) {
                opt.values = freshOpt.values; // new selectable + targetHandle
                changed = true;
              }
            }

            if (changed) {
              const markup = renderPicker(this._schema, this._selection, {
                uid: this._uid,
                currentUrl: this.dataset.url,
                sizeChart: this._sizeChartConfig(),
              });
              const scratch = document.createElement('div');
              scratch.innerHTML = markup;
              for (const opt of linkAxes) {
                const sel = `fieldset[data-pf-option-name="${CSS.escape(opt.name)}"]`;
                const current = this.querySelector(sel);
                const next = scratch.querySelector(sel);
                if (current && next) current.replaceWith(next);
              }
              this._applySelectionToDOM(this._selection);
              this.applyUnitPreferenceToSizePickerDimensions(this);
              this._applyGreying();
            }
            clearLoading();
          });
        },
        () => this._afterMinSpinner(start, clearLoading) // non-fatal
      );
    }

    // ─── DOM sync ─────────────────────────────────────────────────────────

    _applySelectionToDOM(selection) {
      for (const [optName, rawValue] of Object.entries(selection)) {
        // Target the local radio specifically: a same-color swatch from another
        // sub-product renders as a link with the same rawValue, so a bare
        // attribute selector could match that link instead of the radio.
        const input = this.querySelector(
          `input[data-pf-option-name="${CSS.escape(optName)}"][data-pf-raw-value="${CSS.escape(rawValue)}"]`
        );

        if (input && !input.checked) {
          input.checked = true;
        }

        const fieldset = this.querySelector(
          `fieldset[data-pf-option-name="${CSS.escape(optName)}"]`
        );
        if (fieldset) {
          const labelEl = fieldset.querySelector('[data-selected-value-label]');
          if (labelEl) {
            labelEl.textContent = ` - ${this._accessor?.displayLabel(optName, rawValue) ?? rawValue}`;
          }
        }
      }
    }

    _syncUI() {
      this._updateAddToCartButton();
      this._updateStatusClasses();
      this._updateAvailability();
      this._updateSku();
    }

    _updateSku() {
      const sku = this._variant?.sku;
      const el = this._section?.querySelector('.product-sku__value');
      if (!el || !sku) return;
      el.textContent = sku;
      el.closest('.product-sku')?.removeAttribute('hidden');
    }

    // Populate the estimated-ship-time field (formerly driven by the external
    // FulfillmentEstimate AppScript) from the selected variant's availability.
    // The API pre-formats the string ("Ships in 1-2 business days") — render
    // it as-is. Hides the field when the variant has no ship-time.
    _updateAvailability() {
      updateShipTime(this._section, this._variant);
      updateAvailabilityLabel(this._section, this._variant);
    }

    _updateAddToCartButton() {
      updateAddToCartButton(this._section?.querySelector('.js-product-form'), {
        productAvailable: this.classList.contains(
          'variant-picker--product-available'
        ),
        variant: this._variant,
      });
    }

    _updateStatusClasses() {
      this.classList.remove(
        'variant-picker--preselection',
        'variant-picker--pre-last-selection'
      );
    }

    // ─── Compat variant (for base-theme event consumers) ─────────────────

    // Base-theme listeners (media-gallery, inventory) expect a numeric id and a
    // featured_media object; synthesise them from the contract fields.
    _makeCompatVariant() {
      if (!this._variant) return null;
      const v = this._variant;
      return v.featuredMediaId != null
        ? { ...v, featured_media: { id: v.featuredMediaId } }
        : { ...v };
    }

    // ─── Media gallery sync (variant → thumbnails) ──────────────────────────

    // Drive the base <media-gallery> from the selected variant (the API returns
    // featuredMediaId: null, so it can't self-activate). See gallery-sync.js.
    _syncGallery(variantId = this._variant?.id) {
      syncGalleryToVariant(
        this._section?.querySelector('media-gallery'),
        this._variantMedia,
        variantId,
        this._optionAxes(),
        this.dataset.productTitle
      );
    }

    _optionAxes() {
      if (!this._schema || !this._selection) return undefined;
      const isSwatch = (option) => deriveLayout(option) === 'swatch';
      return [...this._schema.options]
        .sort((a, b) => isSwatch(b) - isSwatch(a))
        .map((option) => {
          const label = (value) => value.displayLabel ?? value.rawValue;
          const selected = option.values.find(
            (value) => value.rawValue === this._selection[option.name]
          );
          return {
            names: option.values.map(label),
            selected: selected && label(selected),
          };
        });
    }

    // ─── XHR section render ────────────────────────────────────────────────

    _sectionRender(fallbackFrom) {
      const variantId = this._variant?.id;
      const productUrl = this.dataset.url;
      const sectionId = this.dataset.sectionId;
      if (
        !variantId ||
        !productUrl ||
        !sectionId ||
        !window.theme?.fetchCache
      ) {
        dispatchVariantChange(this, {
          variant: this._makeCompatVariant(),
          selection: this._selection,
          fallbackFrom,
          form: this._section?.querySelector('.js-product-form') ?? null,
        });
        return;
      }

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('sections', sectionId);
      url.searchParams.set('variant', variantId);

      theme.fetchCache
        .fetch(url.toString())
        .then((responseText) => {
          const data = JSON.parse(responseText);
          const sectionHtml = data[sectionId];
          if (!sectionHtml || !this._section) return;

          const newDoc = new DOMParser().parseFromString(
            sectionHtml,
            'text/html'
          );
          const newSection = newDoc.getElementById(
            `shopify-section-${sectionId}`
          );
          if (!newSection) return;

          this._section
            .querySelectorAll('[data-dynamic-variant-content]')
            .forEach((toReplace) => {
              if (toReplace.contains(this)) return; // skip our own container
              const key = toReplace.dataset.dynamicVariantContent;
              const replaceWith = newSection.querySelector(
                `[data-dynamic-variant-content="${CSS.escape(key)}"]`
              );
              if (!replaceWith) return;

              if (toReplace.tagName === 'INPUT' && toReplace.name === 'id') {
                toReplace.value = replaceWith.value;
              } else {
                toReplace.replaceWith(replaceWith);
              }
            });
        })
        .catch(() => {})
        .finally(() => {
          this._syncUI();
          dispatchVariantChange(this, {
            variant: this._makeCompatVariant(),
            selection: this._selection,
            fallbackFrom,
            form: this._section?.querySelector('.js-product-form') ?? null,
          });
        });
    }

    // ─── Unit preference (dimension toggle) ───────────────────────────────

    applyUnitPreferenceToSizePickerDimensions(container) {
      if (!(container instanceof Element)) return;
      const unitPreference = window.Cookies?.getItem?.('pformUnits') ?? null;
      const useMetric = unitPreference === 'Metric';

      container
        .querySelectorAll('[data-layout="x-split"] [data-size-dimensions]')
        .forEach((el) => {
          const metricText = el.getAttribute('data-dimensions-metric') || '';
          const usText = el.getAttribute('data-dimensions-us-standard') || '';
          const next = useMetric && metricText ? metricText : usText;
          if (next) el.textContent = next;
        });
    }
  }

  customElements.define('pf-variant-picker', VariantPicker);
}

// ─── Dimension toggle (unit preference) ────────────────────────────────────
// Runs once per page load regardless of how many pickers are mounted.
if (
  !document.documentElement.hasAttribute(
    'data-pf-picker-dimensions-initialized'
  )
) {
  document.documentElement.setAttribute(
    'data-pf-picker-dimensions-initialized',
    'true'
  );

  function updateSizePickerDimensions() {
    const unitPreference = window.Cookies?.getItem?.('pformUnits') ?? null;
    const useMetric = unitPreference === 'Metric';
    document
      .querySelectorAll('[data-layout="x-split"] [data-size-dimensions]')
      .forEach((el) => {
        const metricText = el.getAttribute('data-dimensions-metric') || '';
        const usText = el.getAttribute('data-dimensions-us-standard') || '';
        const next = useMetric && metricText ? metricText : usText;
        if (next && el.textContent !== next) el.textContent = next;
      });
  }

  document.addEventListener('DOMContentLoaded', updateSizePickerDimensions);
  document.addEventListener('on:variant:change', () =>
    setTimeout(updateSizePickerDimensions, 0)
  );
  document.addEventListener('on:picker:ready', () =>
    setTimeout(updateSizePickerDimensions, 0)
  );
  document.addEventListener('click', (evt) => {
    if (
      evt.target instanceof Element &&
      evt.target.closest('#english, #metric')
    ) {
      setTimeout(updateSizePickerDimensions, 0);
    }
  });
}
